import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@apollo/client/react';
import {
  GetUnitsDocument,
  GetUnitsQuery,
  GetUnitsQueryVariables,
  RecurringExpenseFrequency,
  RecurringExpenseFragmentFragment,
} from '../generated/graphql';
import { AddIcon, RemoveItemIcon } from './icons';

interface UnitAssignmentRow {
  unitId: string;
  amount: string;
}

export interface RecurringExpenseSubmitData {
  title: string;
  amount: number;
  taxDeductible: number;
  refNo?: string;
  frequency: RecurringExpenseFrequency;
  nextDate: string;
  stopAfterDate?: string;
  dueDays: number;
  unitAssignments: { unitId: string; amount: number }[];
}

interface NewRecurringExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RecurringExpenseSubmitData) => void;
  loading: boolean;
  editExpense?: RecurringExpenseFragmentFragment;
  /** When provided, pre-populates unit assignment with this unit */
  initialUnitId?: string;
}

const frequencyOptions: { value: RecurringExpenseFrequency; label: string }[] = [
  { value: 'W', label: 'Týždenne' },
  { value: 'W2', label: 'Každé 2 týždne' },
  { value: 'M', label: 'Mesačne' },
  { value: 'M2', label: 'Každé 2 mesiace' },
  { value: 'M3', label: 'Každé 3 mesiace' },
  { value: 'M6', label: 'Polročne' },
  { value: 'Y', label: 'Ročne' },
];

const dueDaysOptions = Array.from({ length: 31 }, (_, i) => ({ value: i.toString(), label: `${i} dní` }));

interface RecurringExpenseFormValues {
  title: string;
  taxDeductible: string;
  refNo: string;
  frequency: RecurringExpenseFrequency;
  nextDate: string;
  stopAfterDate: string;
  dueDays: string;
}

export function NewRecurringExpenseDialog({
  open,
  onClose,
  onSubmit,
  loading,
  editExpense,
  initialUnitId,
}: NewRecurringExpenseDialogProps) {
  const [amountError, setAmountError] = useState(false);
  const [amountValue, setAmountValue] = useState('');
  const [assignmentRows, setAssignmentRows] = useState<UnitAssignmentRow[]>([]);
  const [assignmentsEdited, setAssignmentsEdited] = useState(false);

  const { data: unitsData } = useQuery<GetUnitsQuery, GetUnitsQueryVariables>(GetUnitsDocument, {
    variables: { includeArchived: false },
  });
  const availableUnits = unitsData?.getUnits?.filter((u) => u.status !== 'archived') ?? [];

  const today = new Date().toISOString().substring(0, 10);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<RecurringExpenseFormValues>({
    defaultValues: {
      title: editExpense?.title ?? '',
      taxDeductible: editExpense?.taxDeductible?.toString() ?? '',
      refNo: editExpense?.refNo ?? '',
      frequency: (editExpense?.frequency as RecurringExpenseFrequency) ?? 'M',
      nextDate: editExpense?.nextDate ? new Date(editExpense.nextDate).toISOString().substring(0, 10) : today,
      stopAfterDate: editExpense?.stopAfterDate
        ? new Date(editExpense.stopAfterDate).toISOString().substring(0, 10)
        : '',
      dueDays: editExpense?.dueDays?.toString() ?? '10',
    },
  });

  useEffect(() => {
    if (open) {
      setAmountError(false);
      setAssignmentsEdited(false);
      const initialAmount = editExpense?.amount?.toString() ?? '';
      setAmountValue(initialAmount);
      if (editExpense?.unitAssignments?.length) {
        setAssignmentRows(editExpense.unitAssignments.map((a) => ({ unitId: a.unitId, amount: a.amount.toString() })));
      } else if (initialUnitId) {
        setAssignmentRows([{ unitId: initialUnitId, amount: initialAmount }]);
      } else {
        setAssignmentRows([]);
      }
      reset({
        title: editExpense?.title ?? '',
        taxDeductible: editExpense?.taxDeductible?.toString() ?? '',
        refNo: editExpense?.refNo ?? '',
        frequency: (editExpense?.frequency as RecurringExpenseFrequency) ?? 'M',
        nextDate: editExpense?.nextDate
          ? new Date(editExpense.nextDate).toISOString().substring(0, 10)
          : new Date().toISOString().substring(0, 10),
        stopAfterDate: editExpense?.stopAfterDate
          ? new Date(editExpense.stopAfterDate).toISOString().substring(0, 10)
          : '',
        dueDays: editExpense?.dueDays?.toString() ?? '10',
      });
    }
  }, [open, editExpense, initialUnitId, reset]);

  const expenseAmount = parseFloat(amountValue) || 0;
  const assignmentTotal = assignmentRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const assignmentTotalMatchesExpense =
    assignmentRows.length === 0 || Math.abs(assignmentTotal - expenseAmount) < 0.001;

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setAmountValue(val);
    setAmountError(!val || isNaN(parseFloat(val)) || parseFloat(val) <= 0);
    if (!assignmentsEdited && assignmentRows.length === 1) {
      setAssignmentRows([{ ...assignmentRows[0], amount: val }]);
    }
  };

  const handleAddAssignmentRow = () => {
    setAssignmentRows((prev) => [...prev, { unitId: '', amount: '' }]);
    setAssignmentsEdited(true);
  };

  const handleRemoveAssignmentRow = (idx: number) => {
    setAssignmentRows((prev) => prev.filter((_, i) => i !== idx));
    setAssignmentsEdited(true);
  };

  const handleAssignmentChange = (idx: number, field: 'unitId' | 'amount', value: string) => {
    setAssignmentRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    setAssignmentsEdited(true);
  };

  const onFormSubmit = (values: RecurringExpenseFormValues) => {
    const amount = parseFloat(amountValue);
    if (!amountValue || isNaN(amount) || amount <= 0) {
      setAmountError(true);
      return;
    }

    const taxDeductibleParsed = values.taxDeductible ? parseFloat(values.taxDeductible) : amount;

    const data: RecurringExpenseSubmitData = {
      title: values.title.trim(),
      amount,
      taxDeductible: isNaN(taxDeductibleParsed) ? amount : taxDeductibleParsed,
      refNo: values.refNo.trim() || undefined,
      frequency: values.frequency,
      nextDate: new Date(values.nextDate).toISOString(),
      dueDays: parseInt(values.dueDays, 10),
      unitAssignments: assignmentRows
        .filter((r) => r.unitId && parseFloat(r.amount) > 0)
        .map((r) => ({ unitId: r.unitId, amount: parseFloat(r.amount) })),
    };

    if (values.stopAfterDate) {
      data.stopAfterDate = new Date(values.stopAfterDate).toISOString();
    }

    onSubmit(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <DialogTitle>{editExpense ? 'Upraviť pravidelný výdavok' : 'Pridať pravidelný výdavok'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  autoFocus
                  label="Názov"
                  {...register('title', { validate: (v) => !!v.trim() || 'Názov je povinný' })}
                  error={!!errors.title}
                  helperText={errors.title?.message ?? ''}
                  fullWidth
                  variant="standard"
                  disabled={loading}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Suma (€)"
                  type="number"
                  required
                  error={amountError}
                  helperText={amountError ? 'Zadajte platnú sumu' : ''}
                  value={amountValue}
                  onChange={handleAmountChange}
                  fullWidth
                  variant="standard"
                  disabled={loading}
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Daňovo uznateľná suma (€)"
                  type="number"
                  {...register('taxDeductible')}
                  fullWidth
                  variant="standard"
                  disabled={loading}
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="standard" required>
                  <InputLabel>Počet dní splatnosti</InputLabel>
                  <Controller
                    name="dueDays"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="Počet dní splatnosti" disabled={loading}>
                        {dueDaysOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Referencia/VS"
                  {...register('refNo')}
                  fullWidth
                  variant="standard"
                  disabled={loading}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth variant="standard" required>
                  <InputLabel>Frekvencia</InputLabel>
                  <Controller
                    name="frequency"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="Frekvencia" disabled={loading}>
                        {frequencyOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Dátum ďalšieho výdavku"
                  type="date"
                  {...register('nextDate', { required: true })}
                  required
                  fullWidth
                  variant="standard"
                  disabled={loading}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Zastaviť spracovanie po dátume"
                  type="date"
                  {...register('stopAfterDate')}
                  fullWidth
                  variant="standard"
                  disabled={loading}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>{/* Placeholder to keep grid layout consistent */}</Grid>
            </Grid>

            <Divider />

            {/* Unit assignments */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Priradenie k nehnuteľnostiam</Typography>
              <Button
                type="button"
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddAssignmentRow}
                disabled={loading}
              >
                Pridať
              </Button>
            </Stack>

            {assignmentRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Výdavok nie je priradený k žiadnej nehnuteľnosti.
              </Typography>
            )}

            <Stack spacing={1}>
              {assignmentRows.map((row, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Select
                      value={row.unitId}
                      onChange={(e) => handleAssignmentChange(idx, 'unitId', e.target.value)}
                      displayEmpty
                      size="small"
                      disabled={loading}
                      sx={{ flex: 1, minWidth: 120 }}
                    >
                      <MenuItem value="">— vybrať —</MenuItem>
                      {availableUnits.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <TextField
                      value={row.amount}
                      onChange={(e) => handleAssignmentChange(idx, 'amount', e.target.value)}
                      type="number"
                      size="small"
                      disabled={loading}
                      label="Suma (€)"
                      inputProps={{ step: '0.01', min: '0.01' }}
                      sx={{ width: 120 }}
                    />
                    <IconButton
                      type="button"
                      size="small"
                      onClick={() => handleRemoveAssignmentRow(idx)}
                      disabled={loading}
                    >
                      <RemoveItemIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            {assignmentRows.length > 0 && expenseAmount > 0 && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" color={assignmentTotalMatchesExpense ? 'success.main' : 'error.main'}>
                  Súčet: {assignmentTotal.toFixed(2)} € / {expenseAmount.toFixed(2)} €
                </Typography>
                {assignmentTotalMatchesExpense && <Chip label="OK" color="success" size="small" />}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose} disabled={loading}>
            Zrušiť
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            Uložiť
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
